import { css, styled } from 'next-yak';

import {
  cornerRadius3XL,
  cornerRadiusXL,
  cornerRadiusXXL,
  scale005,
  scale010,
  space3XL,
  spaceL,
  spaceXL,
  spaceXXL,
  xdlColorA11yBorderOnLight,
  xdlColorCardInspire01,
  xdlColorCardInspire02,
  xdlColorCardInspire03,
  xdlColorCardInspire04,
  xdlColorCardInspire05,
  xdlColorCardInspire06,
  xdlColorCardInspire07,
  xdlColorCardInspire08,
  xdlColorTextOnLight,
} from '@xing-com/tokens';

import { Card } from '../card';
import type { CardPaddings } from '../card';

import type { CardInspireVariants } from './card-inspire.types';

type CardInspireTProps = {
  $isAutoSize?: boolean;
  $padding?: CardPaddings;
  $variant: CardInspireVariants;
};

type CardContainerTProps = {
  $isAutoSize?: boolean;
};

// *** Variants ***
export const variants = {
  1: css`
    background-color: ${xdlColorCardInspire01} !important;
  `,
  2: css`
    background-color: ${xdlColorCardInspire02} !important;
  `,
  3: css`
    background-color: ${xdlColorCardInspire03} !important;
  `,
  4: css`
    background-color: ${xdlColorCardInspire04} !important;
  `,
  5: css`
    background-color: ${xdlColorCardInspire05} !important;
  `,
  6: css`
    background-color: ${xdlColorCardInspire06} !important;
  `,
  7: css`
    background-color: ${xdlColorCardInspire07} !important;
  `,
  8: css`
    background-color: ${xdlColorCardInspire08} !important;
  `,
};

const autoSizeLinkCorners = css`
  & > a {
    border-radius: calc(${cornerRadiusXL} - ${scale010}) !important;

    @container (min-width: 350px) {
      border-radius: calc(${cornerRadiusXXL} - ${scale010}) !important;
    }

    @container (min-width: 560px) {
      border-radius: calc(${cornerRadius3XL} - ${scale010}) !important;
    }
  }
`;

const autoSizeByPadding = {
  regular: css`
    border-radius: ${cornerRadiusXL} !important;
    padding: ${spaceL} !important;
    transition: border-radius 0.3s ease;
    ${autoSizeLinkCorners}

    @container (min-width: 350px) {
      border-radius: ${cornerRadiusXXL} !important;
      padding: ${spaceXL} !important;
    }

    @container (min-width: 560px) {
      border-radius: ${cornerRadius3XL} !important;
      padding: ${spaceXXL} !important;
    }
  `,
  wide: css`
    border-radius: ${cornerRadiusXL} !important;
    padding-block: ${spaceL} !important;
    padding-inline: ${spaceXL} !important;
    transition: border-radius 0.3s ease;
    ${autoSizeLinkCorners}

    @container (min-width: 350px) {
      border-radius: ${cornerRadiusXXL} !important;
      padding-block: ${spaceXL} !important;
      padding-inline: ${spaceXXL} !important;
    }

    @container (min-width: 560px) {
      border-radius: ${cornerRadius3XL} !important;
      padding-block: ${spaceXXL} !important;
      padding-inline: ${space3XL} !important;
    }
  `,
  none: css`
    border-radius: ${cornerRadiusXL} !important;
    padding: 0 !important;
    transition: border-radius 0.3s ease;

    @container (min-width: 350px) {
      border-radius: ${cornerRadiusXXL} !important;
      padding: 0 !important;
    }

    @container (min-width: 560px) {
      border-radius: ${cornerRadius3XL} !important;
      padding: 0 !important;
    }
  `,
} as const;

// *** Components ***
export const CardContainer = styled.div<CardContainerTProps>`
  ${({ $isAutoSize }) => {
    if (!$isAutoSize) return;
    return css`
      container-type: inline-size;
      width: 100%;
    `;
  }}
`;

export const CardInspire = styled(Card)<CardInspireTProps>`
  && {
    border-color: ${xdlColorA11yBorderOnLight};
    border-width: ${scale005};
    color: ${xdlColorTextOnLight};
  }

  ${({ $variant }) => {
    switch ($variant) {
      case 1:
        return variants['1'];
      case 2:
        return variants['2'];
      case 3:
        return variants['3'];
      case 4:
        return variants['4'];
      case 5:
        return variants['5'];
      case 6:
        return variants['6'];
      case 7:
        return variants['7'];
      case 8:
        return variants['8'];
      default:
        return $variant satisfies undefined;
    }
  }}

  ${({ $isAutoSize, $padding = 'wide' }) => {
    if (!$isAutoSize) return;
    switch ($padding) {
      case 'regular':
        return autoSizeByPadding.regular;
      case 'wide':
        return autoSizeByPadding.wide;
      case 'none':
        return autoSizeByPadding.none;
      default:
        return $padding satisfies undefined;
    }
  }}
`;
